import { useMemo, useState } from 'react';
import { ChevronDown, ListFilter } from 'lucide-react';
import clsx from 'clsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card/Card';
import { SectionLabel } from '@/components/ui/section-label';
import { useTranslation } from '@/hooks/useTranslation';
import {
  advancedFields,
  comparisonFields,
  defaultComparisonIndex,
  formatFieldValue,
  scoreDelta,
} from '../lib/experimentView';
import { toolFieldLabelKey } from '../lib/toolFieldLabels';
import type { GaComparison } from '../types/ToolRecommenderTypes';

function scoreText(value: number | null): string {
  return typeof value === 'number' ? formatFieldValue(value) : '—';
}

function optionLabel(row: GaComparison, index: number): string {
  const rank = `#${row.rank ?? index + 1}`;
  const context = [row.materialName, row.operationName].filter(Boolean).join(' · ');
  return `${rank}${context ? ` — ${context}` : ''} — ${scoreText(row.score)}`;
}

interface ExperimentComparisonCardProps {
  comparisons: GaComparison[];
}

export function ExperimentComparisonCard({ comparisons }: ExperimentComparisonCardProps) {
  const { t } = useTranslation();
  const [selectedIndex, setSelectedIndex] = useState(() =>
    defaultComparisonIndex(comparisons)
  );
  const row = comparisons[selectedIndex] ?? comparisons[0];
  const fields = useMemo(() => comparisonFields(row), [row]);
  const advanced = useMemo(() => advancedFields(row), [row]);
  const delta = scoreDelta(row);

  const fieldLabel = (key: string) => {
    const labelKey = toolFieldLabelKey(key);
    return labelKey ? t(labelKey) : key;
  };

  return (
    <Card padding="none" className="overflow-hidden">
      {comparisons.length > 1 && (
        <div className="flex flex-col gap-2 border-b border-border px-6 py-4 sm:flex-row sm:items-center">
          <SectionLabel icon={ListFilter}>{t('toolRecommender.detail.resultLabel')}</SectionLabel>
          <Select
            value={String(selectedIndex)}
            onValueChange={(value) => setSelectedIndex(Number(value))}
          >
            <SelectTrigger className="w-full font-mono text-xs sm:max-w-md">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {comparisons.map((c, index) => (
                <SelectItem key={index} value={String(index)} className="font-mono text-xs">
                  {optionLabel(c, index)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Score duel */}
      <div className="grid items-center gap-4 p-6 sm:grid-cols-[1fr_auto_1fr]">
        <Card tier="inset" padding="none" className="px-5 py-4">
          <p className="eyebrow">{t('toolRecommender.detail.baseScoreLabel')}</p>
          <p className="mt-1.5 font-mono text-3xl font-bold tracking-tight tabular-nums text-foreground">
            {scoreText(row.baseScore)}
          </p>
        </Card>
        <div className="justify-self-center">
          {delta !== null && (
            <Badge
              tone={delta >= 0 ? 'primary' : 'neutral'}
              className="px-3 py-1 font-mono text-sm font-bold tabular-nums"
            >
              {delta >= 0 ? '+' : ''}
              {formatFieldValue(Number(delta.toFixed(2)))}
            </Badge>
          )}
        </div>
        <Card tier="inset" padding="none" className="border-primary/40 bg-primary/10 px-5 py-4">
          <p className="eyebrow">{t('toolRecommender.detail.generatedScoreLabel')}</p>
          <p className="mt-1.5 font-mono text-3xl font-bold tracking-tight tabular-nums text-primary">
            {scoreText(row.score)}
          </p>
        </Card>
      </div>

      {/* Context badges */}
      <div className="flex flex-wrap gap-2 px-6 pb-5">
        {row.materialName && <Badge>{row.materialName}</Badge>}
        {row.operationName && <Badge>{row.operationName}</Badge>}
        {row.baseTool &&
          (row.baseToolArticle ? (
            <Badge>
              {t('toolRecommender.detail.baseFromCatalog')}
              <span className="font-mono">{row.baseToolArticle}</span>
            </Badge>
          ) : (
            <Badge>{t('toolRecommender.detail.baseFromUser')}</Badge>
          ))}
      </div>

      {!row.baseTool && (
        <p className="px-6 pb-4 text-sm text-muted-foreground">
          {t('toolRecommender.detail.noBaseTool')}
        </p>
      )}

      {/* Field-by-field comparison */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-y border-border">
              <th className="eyebrow px-6 py-3 text-left">
                {t('toolRecommender.detail.colField')}
              </th>
              <th className="eyebrow px-6 py-3 text-left">
                {t('toolRecommender.detail.colBase')}
              </th>
              <th className="eyebrow px-6 py-3 text-left">
                {t('toolRecommender.detail.colGenerated')}
              </th>
            </tr>
          </thead>
          <tbody>
            {fields.map((field) => (
              <tr
                key={field.key}
                className={clsx(
                  'border-b border-border last:border-0',
                  field.changed && 'bg-primary/5'
                )}
              >
                <td className="px-6 py-2.5 text-foreground">
                  {field.changed && (
                    <span
                      className="mr-2 inline-block h-1.5 w-1.5 rounded-full bg-primary align-middle"
                      aria-hidden
                    />
                  )}
                  {fieldLabel(field.key)}
                </td>
                <td className="px-6 py-2.5 font-mono font-semibold tabular-nums text-muted-foreground">
                  {formatFieldValue(field.base)}
                </td>
                <td
                  className={clsx(
                    'px-6 py-2.5 font-mono font-semibold tabular-nums',
                    field.changed ? 'text-primary' : 'text-foreground'
                  )}
                >
                  {formatFieldValue(field.generated)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Advanced: base-tool fields the GA does not optimise */}
      {advanced.length > 0 && (
        <Collapsible>
          <CollapsibleTrigger className="group flex w-full items-center gap-2 border-t border-border px-6 py-3.5 text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground">
            <ChevronDown
              className="h-4 w-4 transition-transform group-data-[state=open]:rotate-180"
              aria-hidden
            />
            {t('toolRecommender.detail.advancedTitle').replace(
              '{{n}}',
              String(advanced.length)
            )}
          </CollapsibleTrigger>
          <CollapsibleContent>
            <dl className="grid gap-x-6 gap-y-3 px-6 pb-5 pt-1 sm:grid-cols-3">
              {advanced.map((field) => (
                <div key={field.key}>
                  <dt className="eyebrow">
                    {fieldLabel(field.key)}
                  </dt>
                  <dd className="font-mono text-sm font-semibold tabular-nums text-foreground">
                    {formatFieldValue(field.value)}
                  </dd>
                </div>
              ))}
            </dl>
          </CollapsibleContent>
        </Collapsible>
      )}
    </Card>
  );
}
